import { useEffect, useState } from "react";

import { useGetSadieVoiceBundle } from "@/hooks/queries/useGetSadieVoiceBundle";
import { useGetSadieVoiceSimulatorDefaults } from "@/hooks/queries/useGetSadieVoiceSimulatorDefaults";
import { SadieVoiceCallOptions } from "@/hooks/useSadieVoiceCall";
import {
  SadieVoiceEngine,
  SadieVoiceNote,
  SadieVoiceSipRequest,
} from "@/types";

// Where Live's opening background notes go. "startup": developer messages in
// the session's startup input, in context before Sadie's first word. "pushed":
// session.thinking.append once the session starts, as the v1 README has it,
// which can race her opener.
export type SadieVoiceNotesAt = "startup" | "pushed";

// A Live text field the page can edit. Edits last until the caller setup
// changes, which refills everything.
export type SadieVoiceLiveField =
  | "instructions"
  | "backendInstructions"
  | "postCallPrompt";

// A note the user wrote, given to Sadie after the filled ones. Kept in the
// draft, so it survives caller changes and reloads (in this browser only).
export interface SadieVoiceCustomNote {
  id: string;
  label: string;
  content: string;
}

// What the page remembers between visits, per browser.
interface Draft {
  engine: SadieVoiceEngine;
  models: Partial<Record<SadieVoiceEngine, string>>;
  voice?: string;
  sadieSpeaksFirst: boolean;
  // live
  caller?: string;
  knownCaller: boolean;
  pitch: boolean;
  notesAt: SadieVoiceNotesAt;
  customNotes: SadieVoiceCustomNote[];
  // realtime
  turnDetection?: string;
  reasoningEffort?: string;
  // An absent prompt means the server's.
  realtimePrompt?: string;
}

interface LiveEdits {
  fields: Partial<Record<SadieVoiceLiveField, string>>;
  // By index into the bundle's notes.
  notes: Partial<Record<number, string>>;
}

const NO_EDITS: LiveEdits = { fields: {}, notes: {} };

const DRAFT_KEY = "sadieVoiceSimulatorDraft";

const EMPTY_DRAFT: Draft = {
  engine: "live",
  models: {},
  sadieSpeaksFirst: true,
  knownCaller: true,
  pitch: true,
  notesAt: "startup",
  customNotes: [],
};

const readDraft = (): Draft => {
  try {
    const stored = localStorage.getItem(DRAFT_KEY);
    if (!stored) return EMPTY_DRAFT;
    return { ...EMPTY_DRAFT, ...(JSON.parse(stored) as Partial<Draft>) };
  } catch {
    return EMPTY_DRAFT;
  }
};

// Same rough count as guac's, for notes edited on the page.
const estimateTokens = (text: string) => Math.ceil(text.length / 4);

/** The voice simulator's settings, on top of the server's defaults. */
export function useSadieVoiceSimulator() {
  const defaultsQuery = useGetSadieVoiceSimulatorDefaults();
  const defaults = defaultsQuery.data;
  const [draft, setDraft] = useState<Draft>(readDraft);
  const [liveEdits, setLiveEdits] = useState<LiveEdits>(NO_EDITS);

  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // Storage unavailable: the draft just won't survive a reload.
    }
  }, [draft]);

  // A remembered option the server no longer offers falls back.
  const callers = defaults?.live.callers ?? [];
  const caller = callers.find((c) => c.key === draft.caller) ?? callers.at(0);
  const canPitch = draft.knownCaller && (caller?.hasJobToPitch ?? false);

  const bundleQuery = useGetSadieVoiceBundle({
    caller: caller?.key,
    enabled: draft.engine === "live" && caller !== undefined,
    knownCaller: draft.knownCaller,
    // What the page shows, so a caller who can't be pitched is one fetch.
    pitch: canPitch && draft.pitch,
  });
  const bundle = bundleQuery.data;

  // A new caller setup refills every field, so earlier edits no longer apply.
  useEffect(() => {
    setLiveEdits(NO_EDITS);
  }, [bundle]);

  const liveField = (field: SadieVoiceLiveField) =>
    liveEdits.fields[field] ?? bundle?.[field] ?? "";
  const isLiveFieldEdited = (field: SadieVoiceLiveField) =>
    liveEdits.fields[field] !== undefined &&
    liveEdits.fields[field] !== bundle?.[field];
  const notes: (SadieVoiceNote & { edited: boolean })[] = (
    bundle?.notes ?? []
  ).map((note, i) => {
    const edit = liveEdits.notes[i];
    const content = edit ?? note.content;
    return {
      ...note,
      content,
      tokens: edit === undefined ? note.tokens : estimateTokens(content),
      edited: edit !== undefined && edit !== note.content,
    };
  });

  const engineDefaults = defaults?.[draft.engine];
  const rememberedModel = draft.models[draft.engine];
  const model =
    rememberedModel && engineDefaults?.models.includes(rememberedModel)
      ? rememberedModel
      : engineDefaults?.defaultModel;
  const voice =
    draft.voice && defaults?.voices.includes(draft.voice)
      ? draft.voice
      : defaults?.defaultVoice;
  const turnDetection =
    draft.turnDetection &&
    defaults?.realtime.turnDetections.some((t) => t.key === draft.turnDetection)
      ? draft.turnDetection
      : defaults?.realtime.defaultTurnDetection;
  const reasoningEffort =
    draft.reasoningEffort &&
    defaults?.realtime.reasoningEfforts.includes(draft.reasoningEffort)
      ? draft.reasoningEffort
      : defaults?.realtime.defaultReasoningEffort;
  const realtimePrompt =
    draft.realtimePrompt ?? defaults?.realtime.instructions ?? "";

  const update = (patch: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  // Every note the call starts with: the filled ones, then the user's own.
  // A custom note with no text is left out.
  const callNotes = [
    ...notes.map((n) => ({ key: n.key, label: n.label, content: n.content })),
    ...draft.customNotes
      .filter((n) => n.content.trim())
      .map((n) => ({
        key: "Custom",
        label: n.label.trim() || "Untitled",
        content: n.content,
      })),
  ];

  const updateCustomNotes = (
    change: (notes: SadieVoiceCustomNote[]) => SadieVoiceCustomNote[],
  ) => {
    setDraft((current) => ({
      ...current,
      customNotes: change(current.customNotes),
    }));
  };

  // A browser call and a phone call run the same Live setup.
  const liveSetup = () => ({
    instructions: liveField("instructions"),
    backendInstructions: liveField("backendInstructions"),
    model,
    voice,
    // OpenAI's way to have Live speak first is a startup developer
    // message, so the opener cue always goes in last.
    startupMessages: [
      ...(draft.notesAt === "startup" ? callNotes.map((n) => n.content) : []),
      ...(draft.sadieSpeaksFirst && bundle ? [bundle.openerCue] : []),
    ],
  });

  const liveCallOptions = (): SadieVoiceCallOptions => {
    return {
      engine: "live",
      ...liveSetup(),
      onReady:
        draft.notesAt === "startup"
          ? [
              {
                label: `Started with ${callNotes.map((n) => n.key).join(", ")} in the startup input`,
              },
            ]
          : callNotes.map((n) => ({
              event: {
                type: "session.thinking.append",
                content: n.content,
                delegation_id: null,
              },
              label: `Pushed ${n.key}: ${n.label}`,
            })),
    };
  };

  return {
    defaults,
    defaultsStatus: defaultsQuery.status,
    refetchDefaults: defaultsQuery.refetch,
    engine: draft.engine,
    model,
    models: engineDefaults?.models ?? [],
    voice,
    voices: defaults?.voices ?? [],
    sadieSpeaksFirst: draft.sadieSpeaksFirst,
    // live
    bundle,
    bundleStatus: bundleQuery.status,
    callers,
    caller: caller?.key,
    knownCaller: draft.knownCaller,
    canPitch,
    pitch: canPitch && draft.pitch,
    notesAt: draft.notesAt,
    notes,
    customNotes: draft.customNotes.map((n) => ({
      ...n,
      tokens: estimateTokens(n.content),
    })),
    liveField,
    isLiveFieldEdited,
    // realtime
    turnDetection,
    turnDetections: defaults?.realtime.turnDetections ?? [],
    reasoningEffort,
    reasoningEfforts: defaults?.realtime.reasoningEfforts ?? [],
    realtimePrompt,
    isRealtimePromptEdited:
      draft.realtimePrompt !== undefined &&
      draft.realtimePrompt !== defaults?.realtime.instructions,

    setEngine: (engine: SadieVoiceEngine) => {
      update({ engine });
    },
    setModel: (value: string) => {
      setDraft((current) => ({
        ...current,
        models: { ...current.models, [current.engine]: value },
      }));
    },
    setVoice: (value: string) => {
      update({ voice: value });
    },
    setSadieSpeaksFirst: (value: boolean) => {
      update({ sadieSpeaksFirst: value });
    },
    setCaller: (value: string) => {
      update({ caller: value });
    },
    setKnownCaller: (value: boolean) => {
      update({ knownCaller: value });
    },
    setPitch: (value: boolean) => {
      update({ pitch: value });
    },
    addCustomNote: () => {
      updateCustomNotes((current) => [
        ...current,
        { id: crypto.randomUUID(), label: "", content: "" },
      ]);
    },
    updateCustomNote: (
      id: string,
      patch: Partial<Pick<SadieVoiceCustomNote, "label" | "content">>,
    ) => {
      updateCustomNotes((current) =>
        current.map((n) => (n.id === id ? { ...n, ...patch } : n)),
      );
    },
    removeCustomNote: (id: string) => {
      updateCustomNotes((current) => current.filter((n) => n.id !== id));
    },
    setNotesAt: (value: SadieVoiceNotesAt) => {
      update({ notesAt: value });
    },
    setLiveField: (field: SadieVoiceLiveField, value: string) => {
      setLiveEdits((current) => ({
        ...current,
        fields: { ...current.fields, [field]: value },
      }));
    },
    resetLiveField: (field: SadieVoiceLiveField) => {
      setLiveEdits((current) => ({
        ...current,
        fields: { ...current.fields, [field]: undefined },
      }));
    },
    setNote: (index: number, value: string) => {
      setLiveEdits((current) => ({
        ...current,
        notes: { ...current.notes, [index]: value },
      }));
    },
    resetNote: (index: number) => {
      setLiveEdits((current) => ({
        ...current,
        notes: { ...current.notes, [index]: undefined },
      }));
    },
    setTurnDetection: (value: string) => {
      update({ turnDetection: value });
    },
    setReasoningEffort: (value: string) => {
      update({ reasoningEffort: value });
    },
    setRealtimePrompt: (value: string) => {
      update({ realtimePrompt: value });
    },
    // Back to the server's.
    resetRealtimePrompt: () => {
      update({ realtimePrompt: undefined });
    },
    /** What a call started now would run. */
    callOptions: (): SadieVoiceCallOptions =>
      draft.engine === "live"
        ? liveCallOptions()
        : {
            engine: "realtime",
            instructions: realtimePrompt,
            model,
            voice,
            turnDetection,
            reasoningEffort,
            // Realtime takes response.create as "say something now"; with no
            // conversation yet, that is the opener the prompt describes.
            onReady: draft.sadieSpeaksFirst
              ? [{ event: { type: "response.create" } }]
              : [],
          },
    /** The Live setup inbound phone calls are answered with. */
    inboundCallSetup: (): SadieVoiceSipRequest => ({
      ...liveSetup(),
      ...(draft.notesAt === "pushed"
        ? { pushedNotes: callNotes.map((n) => n.content) }
        : {}),
    }),
  };
}
